import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

// Poster accent (from the approved mockup — slightly brighter than the app brand).
const ACCENT = '#F5960A';
// Poster fonts (already linked in index.html / global CSS).
const HIND = '"Hind Siliguri", "Noto Sans Bengali", sans-serif';
const NOTO = '"Noto Sans Bengali", "Hind Siliguri", sans-serif';

// Per-office poster preferences — which optional bits (shop logo / address /
// phone) are SHOWN on the poster. Logo & address default OFF, phone defaults ON.
// Stored locally — a render-time choice, not booth config, so no backend round-trip.
const POSTER_PREF_DEFAULTS = { showLogo: false, showAddress: false, showPhone: true };
const posterPrefsKey = (officeSpaceId) => `sk_booth_poster_${officeSpaceId}`;
export const readBoothPosterPrefs = (officeSpaceId) => {
    try { return { ...POSTER_PREF_DEFAULTS, ...(JSON.parse(localStorage.getItem(posterPrefsKey(officeSpaceId))) || {}) }; } catch { return { ...POSTER_PREF_DEFAULTS }; }
};
export const saveBoothPosterPrefs = (officeSpaceId, prefs) => {
    try { localStorage.setItem(posterPrefsKey(officeSpaceId), JSON.stringify(prefs)); } catch { /* ignore */ }
};

const loadImg = (src) => new Promise((res) => {
    if (!src) return res(null);
    const im = new window.Image();
    im.onload = () => res(im);
    im.onerror = () => res(null);
    im.src = src;
});

// Canvas stand-in for CSS color-mix(): blend a hex toward black (0) or white (255).
const mixHex = (hex, target, p) => {
    const v = (i) => parseInt(hex.slice(i, i + 2), 16);
    const m = (a) => Math.round(a * (1 - p) + target * p);
    return `rgb(${m(v(1))},${m(v(3))},${m(v(5))})`;
};
const shade = (hex, p) => mixHex(hex, 0, p);
const tint = (hex, p) => mixHex(hex, 255, p);

const roundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
};
const poly = (ctx, pts) => {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
};

// Build the Photo-Booth poster as a PNG data URL (mockup design). `shopLogoDataUrl`
// must already be a same-origin data URL (R2 logos are proxied by the caller) so
// the canvas stays untainted. Layout, top → bottom:
//   • angled accent backdrop (top 40% + centre chevron to 64%) with subtle facets
//   • shop logo tile + shop name on ONE line (name auto-shrinks/truncates)
//   • headline + sub — "Send your photo", or "Send photo / file" when the shop also receives PDFs (`acceptFiles`)
//   • white QR card with soft shadow
//   • phone ("মোবা:"/"Mob:" prefix) + address
//   • SohozKaj logo in an accent-bordered frame
//   • dark-accent footer bar: site URL (left) + phone (right)
export async function buildBoothPosterDataUrl({ uploadUrl, shopName, shopLogoDataUrl, shopPhone, shopAddress, acceptFiles = false, t }) {
    const W = 800, H = 1160, QR = 436;
    const qrData = await QRCode.toDataURL(uploadUrl, { width: QR, margin: 1 });
    const qrImg = await loadImg(qrData);
    const skLogo = await loadImg('/images/logo.svg');
    const shopImg = await loadImg(shopLogoDataUrl);
    try {
        await Promise.all([
            document.fonts.load(`800 62px ${NOTO}`), document.fonts.load(`800 50px ${NOTO}`),
            document.fonts.load(`800 36px ${NOTO}`), document.fonts.load(`500 28px ${HIND}`),
            document.fonts.load(`600 22px ${HIND}`),
        ]);
    } catch { /* draw with fallback */ }

    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);

    // ── Backdrop: accent shape (top 40% + centre chevron down to 64%) with the
    //    mockup's light/dark facet overlays, all clipped to the shape. ──
    const px = (p) => (p / 100) * W, py = (p) => (p / 100) * H;
    ctx.save();
    poly(ctx, [[0, 0], [W, 0], [W, py(40)], [px(72), py(40)], [px(50), py(64)], [px(28), py(40)], [0, py(40)]]);
    ctx.clip();
    const grad = ctx.createLinearGradient(228, -61, 572, 1221); // ≈ CSS 165deg across the full box
    grad.addColorStop(0, tint(ACCENT, 0.06));
    grad.addColorStop(1, shade(ACCENT, 0.10));
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
    const facets = [
        ['rgba(255,255,255,0.10)', [[0, 0], [px(44), 0], [px(10), py(40)], [0, py(52)]]],
        ['rgba(0,0,0,0.10)', [[px(58), 0], [W, 0], [W, py(34)]]],
        ['rgba(0,0,0,0.08)', [[0, py(20)], [px(22), py(40)], [0, py(52)]]],
        ['rgba(0,0,0,0.09)', [[W, py(24)], [W, py(40)], [px(70), py(40)], [px(50), py(78)]]],
        ['rgba(255,255,255,0.07)', [[px(26), 0], [px(58), 0], [px(34), py(40)], [px(22), py(40)]]],
    ];
    facets.forEach(([fill, pts]) => { poly(ctx, pts); ctx.fillStyle = fill; ctx.fill(); });
    ctx.restore();

    // ── Header: logo tile + shop name on ONE line (name shrinks/truncates to fit) ──
    let y = 40;
    const tile = 96, gap = 26;
    const name = (shopName || 'SohozKaj').trim();
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    let fs = 50;
    const availName = W - 80 - (shopImg ? tile + gap : 0);
    ctx.font = `800 ${fs}px ${NOTO}`;
    let nw = ctx.measureText(name).width;
    if (nw > availName) { fs = Math.max(24, Math.floor(fs * availName / nw)); ctx.font = `800 ${fs}px ${NOTO}`; nw = ctx.measureText(name).width; }
    let disp = name;
    if (nw > availName) { while (disp.length > 1 && ctx.measureText(disp + '…').width > availName) disp = disp.slice(0, -1); disp += '…'; nw = ctx.measureText(disp).width; }
    const groupW = (shopImg ? tile + gap : 0) + nw;
    let gx = (W - groupW) / 2;
    if (shopImg) {
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.15)'; ctx.shadowBlur = 14; ctx.shadowOffsetY = 4;
        roundRect(ctx, gx, y, tile, tile, 14);
        ctx.fillStyle = '#f7efdf'; ctx.fill();
        ctx.restore();
        // Logo kept to its own aspect inside an 82×82 rounded window.
        const iw = shopImg.naturalWidth || 1, ih = shopImg.naturalHeight || 1;
        const s = Math.min(82 / iw, 82 / ih);
        const lw = iw * s, lh = ih * s;
        ctx.save();
        roundRect(ctx, gx + 7, y + 7, 82, 82, 10);
        ctx.clip();
        ctx.drawImage(shopImg, gx + (tile - lw) / 2, y + (tile - lh) / 2, lw, lh);
        ctx.restore();
        gx += tile + gap;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillText(disp, gx, y + tile / 2 + 2);
    y += tile;

    // ── Headline + sub ──
    ctx.textAlign = 'center';
    y += 38;
    const headline = t(acceptFiles ? 'photoCollectorPanel.posterHeadlineFiles' : 'photoCollectorPanel.posterHeadline');
    let hs = 62;
    ctx.font = `800 ${hs}px ${NOTO}`; ctx.fillStyle = '#ffffff';
    const hw = ctx.measureText(headline).width;
    if (hw > W - 80) { hs = Math.max(34, Math.floor(hs * (W - 80) / hw)); ctx.font = `800 ${hs}px ${NOTO}`; } // a longer wording never runs off the poster
    ctx.fillText(headline, W / 2, y + 39);
    y += 78 + 6;
    ctx.font = `500 28px ${HIND}`; ctx.fillStyle = '#fff7ea';
    ctx.fillText(t('photoCollectorPanel.posterSub'), W / 2, y + 17);
    y += 34;

    // ── White QR card with soft shadow ──
    y += 28;
    const card = QR + 44;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.16)'; ctx.shadowBlur = 34; ctx.shadowOffsetY = 12;
    roundRect(ctx, (W - card) / 2, y, card, card, 6);
    ctx.fillStyle = '#ffffff'; ctx.fill();
    ctx.restore();
    if (qrImg) ctx.drawImage(qrImg, (W - QR) / 2, y + 22, QR, QR);
    y += card;

    // ── Phone + address ──
    const phone = (shopPhone || '').toString().trim();
    const addr = (shopAddress || '').toString().trim();
    if (phone) {
        y += 26;
        ctx.font = `800 36px ${NOTO}`; ctx.fillStyle = '#222222';
        ctx.fillText(`${t('photoCollectorPanel.posterPhonePrefix')} ${phone}`, W / 2, y + 23);
        y += 46;
    }
    if (addr) {
        y += phone ? 4 : 26;
        ctx.font = `500 26px ${HIND}`; ctx.fillStyle = '#555555';
        ctx.fillText(addr.slice(0, 64), W / 2, y + 17);
        y += 34;
    }

    // ── SohozKaj logo in an accent-bordered frame ──
    if (skLogo) {
        y += 18;
        const lnw = skLogo.naturalWidth || 512, lnh = skLogo.naturalHeight || 125;
        let lh = 52, lw = lh * (lnw / lnh);
        if (lw > 260) { lh = lh * (260 / lw); lw = 260; }
        const bw = lw + 68, bh = 84;
        roundRect(ctx, (W - bw) / 2, y, bw, bh, 16);
        ctx.strokeStyle = shade(ACCENT, 0.08); ctx.lineWidth = 2; ctx.stroke();
        ctx.drawImage(skLogo, (W - lw) / 2, y + (bh - lh) / 2, lw, lh);
    }

    // ── Footer bar (pinned to the bottom edge) ──
    const fh = 58, fy = H - fh;
    ctx.fillStyle = shade(ACCENT, 0.16); ctx.fillRect(0, fy, W, fh);
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left'; ctx.font = `600 21px ${HIND}`;
    ctx.letterSpacing = '2px';
    ctx.fillText('WWW.SOHOZKAJ.COM', 38, fy + fh / 2 + 1);
    ctx.letterSpacing = '0px';
    if (phone) {
        ctx.textAlign = 'right'; ctx.font = `600 22px ${HIND}`;
        ctx.fillText(`${t('photoCollectorPanel.posterPhonePrefix')} ${phone}`, W - 38, fy + fh / 2 + 1);
    }
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';

    return canvas.toDataURL('image/png');
}

export async function downloadBoothPoster(opts) {
    const dataUrl = await buildBoothPosterDataUrl(opts);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'Sohozkaj-PhotoBooth-Poster-QR.png';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
}

// Print the poster directly in the browser (its own iframe → no app-CSS leak).
export async function printBoothPoster(opts) {
    const dataUrl = await buildBoothPosterDataUrl(opts);
    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    // The img box is sized to EXACTLY one page (100vh) with the poster contained
    // inside — otherwise the 800×1160 poster overflows A4 height and prints a
    // second, blank page.
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><style>@page{margin:0}html,body{margin:0;padding:0;height:100%;overflow:hidden}img{display:block;width:100%;height:100vh;object-fit:contain;object-position:top center}</style></head><body><img src="${dataUrl}"></body></html>`);
    doc.close();
    const cleanup = () => setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* gone */ } }, 1500);
    const go = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ } cleanup(); };
    const img = doc.querySelector('img');
    if (img && !img.complete) img.onload = () => setTimeout(go, 60); else setTimeout(go, 120);
}

// Poster preview — renders the ACTUAL poster canvas (pixel-identical to the
// downloaded/printed PNG), so what you see is exactly what prints.
export function BoothPosterPreview({ uploadUrl, shopName, shopLogoDataUrl, shopPhone, shopAddress, acceptFiles = false, className = '' }) {
    const { t } = useTranslation();
    const [dataUrl, setDataUrl] = useState(null);
    useEffect(() => {
        let alive = true;
        if (!uploadUrl) { setDataUrl(null); return; }
        buildBoothPosterDataUrl({ uploadUrl, shopName, shopLogoDataUrl, shopPhone, shopAddress, acceptFiles, t })
            .then(d => { if (alive) setDataUrl(d); })
            .catch(() => {});
        return () => { alive = false; };
    }, [uploadUrl, shopName, shopLogoDataUrl, shopPhone, shopAddress, acceptFiles, t]);

    return (
        <div className={`rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700 shadow-md bg-white ${className}`} style={{ aspectRatio: '800 / 1160' }}>
            {dataUrl
                ? <img src={dataUrl} alt="Poster" className="w-full h-full object-contain" />
                : <div className="w-full h-full flex items-center justify-center bg-zinc-50 dark:bg-zinc-800"><span className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>}
        </div>
    );
}
